import { jwtDecode } from "jwt-decode"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import { DateTime } from "luxon"

import { seconds } from "@/3514/utils/time.utils"
import { EStorageServiceType, LocalStorageServiceKeys, SessionStorageServiceKeys, StorageService } from "@/services"

const localStorageService: StorageService = new StorageService(EStorageServiceType.Local)
const sessionStorageService: StorageService = new StorageService(EStorageServiceType.Session)

function getCurrentAccessToken(): string {
    let token: string = sessionStorageService.getItem<string>(SessionStorageServiceKeys.FakeParticipantTokenAccess())

    if (isEmpty(token) || isNull(token)) {
        token = localStorageService.getItem<string>(LocalStorageServiceKeys.TokenAccess())
    }

    return token
}

function getCurrentRefreshToken(): string {
    let token: string = sessionStorageService.getItem<string>(SessionStorageServiceKeys.FakeParticipantTokenRefresh())

    if (isEmpty(token) || isNull(token)) {
        token = localStorageService.getItem<string>(LocalStorageServiceKeys.TokenRefresh())
    }

    return token
}

function setCurrentAccessToken(accessToken: string): void {
    if (accessToken) {
        if (sessionStorageService.getItem(SessionStorageServiceKeys.FakeParticipantsFeatureEnabled()))
            sessionStorageService.setItem(SessionStorageServiceKeys.FakeParticipantTokenAccess(), accessToken)
        else localStorageService.setItem(LocalStorageServiceKeys.TokenAccess(), accessToken)
    }
}

async function setCurrentRefreshToken(refreshToken: string): Promise<void> {
    if (refreshToken) {
        if (sessionStorageService.getItem(SessionStorageServiceKeys.FakeParticipantsFeatureEnabled()))
            sessionStorageService.setItem(SessionStorageServiceKeys.FakeParticipantTokenRefresh(), refreshToken)
        else localStorageService.setItem(LocalStorageServiceKeys.TokenRefresh(), refreshToken)
    }
}

function _base64UrlDecode(str: string): string {
    try {
        return decodeURIComponent(
            atob(str.replace(/-/g, "+").replace(/_/g, "/"))
                .split("")
                .map((c: string): string => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join("")
        )
    } catch (e) {
        return ""
    }
}

function isJwtTokenValid(token: string): boolean {
    if (isEmpty(token)) return false

    const parts: string[] = token.split(".")

    if (parts.length !== 3) return false

    const header: string = _base64UrlDecode(parts[0])
    const payload: string = _base64UrlDecode(parts[1])

    try {
        JSON.parse(header)
        JSON.parse(payload)
        return true
    } catch (e) {
        return false
    }
}

function isJwtTokenLive(token: string, advanceTimeInMs = undefined): boolean {
    try {
        if (advanceTimeInMs) {
            return (
                DateTime.now().toMillis() <=
                DateTime.fromSeconds(jwtDecode(token).exp)
                    .minus({ seconds: advanceTimeInMs / 1000 })
                    .toMillis()
            )
        }

        return DateTime.now().toMillis() <= DateTime.fromSeconds(jwtDecode(token).exp).toMillis()
    } catch (error) {
        return false
    }
}

function isAccessTokenValid(): boolean {
    const token: string = getCurrentAccessToken()

    return isJwtTokenValid(token) && isJwtTokenLive(token)
}

function isRefreshTokenValid(): boolean {
    const token: string = getCurrentRefreshToken()

    return isJwtTokenValid(token) && isJwtTokenLive(token, seconds(30))
}

export {
    isJwtTokenLive,
    isJwtTokenValid,
    isAccessTokenValid,
    isRefreshTokenValid,
    getCurrentAccessToken,
    getCurrentRefreshToken,
    setCurrentAccessToken,
    setCurrentRefreshToken
}
